/**
 * ChatMessage - Composant de message optimisé pour les performances
 *
 * Optimisations implémentées :
 * - Mémoisation avec React.memo et comparaison personnalisée
 * - useMemo pour les calculs coûteux
 * - useCallback pour les handlers
 * - Lazy loading des avatars
 * - Optimisations CSS pour l'accélération GPU
 */
import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import classNames from "classnames";
import "./ChatMessage.style.scss";
import * as moment from "moment";
import "moment/locale/fr";
import { InlineIcon } from "@iconify/react";
import TextDisplayer from "../TextDisplayer/TextDisplayer";
import UserInfoPopover from "../UserInfoPopover/UserInfoPopover";
import { socketChannels } from "@/config/socketChannels";
import useExchangeRole from "@/hooks/useExchangeRole";
import { AppSocketContext } from "@/routes";
import { store } from "@/store";
import {
  updateNbreMessageSent,
  updateNbreMessageReceive,
} from "@/store/reducers/general_param/general_param.actions";
import {
  selectMaxMessage,
  selectNbreMessageSent,
  selectNbreMessageReceive,
} from "@/store/reducers/general_param/general_param.selector";
import { useSelector } from "react-redux";
import { calculerAge } from "@/utils/calculerAge";

export type TChatMessageProps = {
  _id?: string;
  type: "sent" | "received";
  gender: "MALE" | "FEMALE";
  fromStaff: boolean;
  fromAdmin?: boolean;
  time: string;
  pending: boolean;
  message: string;
  isValid: boolean;
  reason: string;
  note?: string;
  isRead?: boolean;
  issueDate?: string;
};

const ChatMessage: React.FC<TChatMessageProps> = React.memo(
  ({
    _id,
    type,
    gender,
    fromStaff,
    fromAdmin,
    pending,
    message,
    isValid,
    reason,
    time = moment.default(new Date()).format("LL") +
      " " +
      moment.default(new Date()).format("LT"),
    note = "",
    isRead = false,
    issueDate,
  }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [userData, setUserData] = useState<{
      username: string;
      age: number;
      city: string;
      country: string;
      messagesLeft?: string;
      duration?: string;
      messageMax?: string;
      hasSubscription?: boolean;
    } | null>(null);

    const { sender, receiver, currentExchange } = useExchangeRole();
    const max_message = useSelector(selectMaxMessage);
    const nbre_message_sent = useSelector(selectNbreMessageSent);
    const nbre_message_receive = useSelector(selectNbreMessageReceive);

    const { socketIO } = useContext(AppSocketContext);

    useEffect(() => {
      //console.log('||| ⭐️',socketIO);

      if (socketIO) {
        socketIO.on(socketChannels.nbre_message_available_count, (data) => {
          console.log(data);
          store.dispatch(updateNbreMessageSent(data.sender));
          store.dispatch(updateNbreMessageReceive(data.receiver));
        });
      } else {
        console.log("⭐️ CLOSED : ", socketIO);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socketIO]);

    // Mémoisation de l'avatar pour éviter les recalculs
    const avatarSrc = useMemo(() => {
      if (fromStaff || fromAdmin) return null;
      return require(`@/assets/images/png/avatar-${gender}.png`);
    }, [fromStaff, fromAdmin, gender]);

    const handleAvatarClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>, gender: "MALE" | "FEMALE") => {
        const temp_userData = {
          username:
            sender?.gender === gender ? sender?.username : receiver?.username,
          age: calculerAge(
            new Date(
              sender?.gender === gender
                ? sender?.birthDate
                : receiver?.birthDate || ""
            )
          ),
          city: sender?.gender === gender ? sender?.city : receiver?.city,
          country:
            sender?.gender === gender ? sender?.country : receiver?.country,
          messagesLeft:
            sender?.gender === gender
              ? max_message - nbre_message_sent
              : max_message - nbre_message_receive,
          duration:
            Math.abs(
              moment
                .default(currentExchange?.responseDate || currentExchange?.date)
                .diff(moment.default(new Date()), "days")
            ) + " jrs",
          messageMax: max_message,
        };

        setUserData({
          username: temp_userData.username || "",
          age: temp_userData.age,
          city: temp_userData.city || "",
          country: temp_userData.country || "",
          messagesLeft: String(temp_userData.messagesLeft),
          duration: temp_userData.duration,
          messageMax: String(temp_userData.messageMax),
          hasSubscription:
            sender?.gender === gender
              ? sender?.hasSubscription
              : receiver?.hasSubscription,
        });

        setAnchorEl(event.currentTarget);
      },
      [
        sender,
        receiver,
        max_message,
        nbre_message_sent,
        nbre_message_receive,
        currentExchange,
      ]
    );

    const handlePopoverClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    // Mémoisation pour déterminer si l'abonnement est expiré
    const isSubscriptionExpired = useMemo(() => {
      if (fromStaff || fromAdmin) return false;
      const userHasSubscription =
        sender?.gender === gender
          ? sender?.hasSubscription
          : receiver?.hasSubscription;
      return userHasSubscription === false;
    }, [fromStaff, fromAdmin, sender, receiver, gender]);

    const Avatar = useCallback(() => {
      if (!avatarSrc) return null;

      return (
        <div
          className={classNames("message-avatar", {
            clickable: !!userData,
            "subscription-expired": isSubscriptionExpired,
          })}
          onClick={(e) => handleAvatarClick(e, gender)}
          style={{
            // Optimisations légères pour l'avatar
            backfaceVisibility: "hidden",
          }}
        >
          <img
            src={avatarSrc}
            alt={`Avatar ${gender}`}
            loading="lazy"
            decoding="async"
          />
          {isSubscriptionExpired && (
            <div className="subscription-warning">
              <InlineIcon icon="mdi:alert" />
            </div>
          )}
        </div>
      );
    }, [avatarSrc, userData, gender, handleAvatarClick, isSubscriptionExpired]);

    const MessageContent = useCallback(
      () => (
        <div
          className="c-message-layout__container"
          style={{
            // Optimisations légères pour le contenu
            backfaceVisibility: "hidden",
          }}
        >
          {fromStaff ? (
            <>
              {fromAdmin && (
                <p className="title">
                  <img
                    src={require("@/assets/images/png/logo-mini.png")}
                    alt="logo"
                    style={{ height: "25px", width: "auto" }}
                  />
                  Équipe Zawaj Sounnah
                </p>
              )}
              <p className={fromAdmin ? "p" : "rejected"}>
                {fromAdmin ? null : "Service de modération"}
              </p>
              {!fromAdmin && <p className="p">{message}</p>}
              {fromAdmin && (
                <TextDisplayer
                  isForPopper={true}
                  className={"p"}
                  value={message}
                />
              )}
              <small className="message-time-moderation">{time}</small>
            </>
          ) : (
            <>
              {pending ? (
                <>
                  <TextDisplayer
                    isForPopper={true}
                    className={classNames("p", { pending: pending })}
                    value={message}
                  />
                  <div className="message-meta">
                    <small className="message-time">{time}</small>
                  </div>
                </>
              ) : isValid ? (
                <>
                  <TextDisplayer
                    className={classNames("p", { valid: isValid })}
                    isForPopper={true}
                    value={message}
                  />
                  <div className="message-meta">
                    <small className="message-time">{time}</small>
                    {type === "sent" && (
                      <p className={`checked ${isRead ? "read" : ""}`}>
                        <InlineIcon
                          icon="akar-icons:double-check"
                          style={{
                            color: isRead ? "#1976d2" : "inherit",
                            transition: "color 0.2s ease",
                          }}
                        />
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <TextDisplayer
                    className="p pending"
                    style={{ marginBottom: "5px" }}
                    isForPopper={true}
                    value={message}
                  />
                  <p className="rejected">Ce message a été rejeté</p>
                  <p className="reason">
                    Raison: <b>{reason}</b>
                  </p>
                  <div className="message-meta">
                    <small className="message-time">{time}</small>
                    {type === "sent" && (
                      <p className={`checked ${isRead ? "read" : ""}`}>
                        <InlineIcon
                          icon="akar-icons:double-check"
                          style={{
                            color: isRead ? "#1976d2" : "inherit",
                            transition: "color 0.2s ease",
                          }}
                        />
                      </p>
                    )}
                  </div>
                </>
              )}
              {pending && (
                <p className="in-progress">En cours de moderation ...</p>
              )}
              {note && note.length > 0 && (
                <div className="note">
                  <small>Note du modérateur :</small>
                  <span>{note}</span>
                </div>
              )}
            </>
          )}
        </div>
      ),
      [
        fromStaff,
        fromAdmin,
        message,
        time,
        pending,
        isValid,
        type,
        isRead,
        reason,
        note,
      ]
    );

    return (
      <>
        <div
          className={classNames("c-message-layout", {
            sent: type === "sent",
            received: type === "received",
            male: gender === "MALE",
            female: gender === "FEMALE",
            staff: fromStaff,
            admin: fromAdmin,
          })}
          style={{
            // Optimisations légères pour éviter les problèmes de performance
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {type === "received" && !fromStaff && !fromAdmin && <Avatar />}
          <MessageContent />
          {type === "sent" && !fromStaff && !fromAdmin && <Avatar />}
        </div>

        {userData && (
          <UserInfoPopover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            userData={{
              ...userData,
              avatar: avatarSrc,
            }}
          />
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Comparaison personnalisée pour optimiser les re-renders
    return (
      prevProps._id === nextProps._id &&
      prevProps.isRead === nextProps.isRead &&
      prevProps.pending === nextProps.pending &&
      prevProps.isValid === nextProps.isValid &&
      prevProps.message === nextProps.message &&
      prevProps.type === nextProps.type &&
      prevProps.time === nextProps.time
    );
  }
);

export default ChatMessage;
